"use client"

import { useEffect, useMemo, useRef, useState } from "react"


/* ───────────────────────────────────────────────────────────────────────────
   HeroLiveValidator — single-email inline widget for the hero.
   Real regex + curated lists (disposable, role, typos). 6-check verdict.
   The full CSV pipeline lives at /try.
   ─────────────────────────────────────────────────────────────────────────── */

// Known consumer email providers — anything that's an exact match here
// passes MX trivially. Anything that looks LIKE one of these but isn't
// gets flagged as a typo via the Levenshtein + repeated-char checks.
const KNOWN_PROVIDERS = [
  "gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com",
  "aol.com", "live.com", "msn.com", "protonmail.com", "proton.me",
  "yandex.com", "mail.com", "zoho.com", "gmx.com", "fastmail.com",
]

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length
  if (m === 0) return n
  if (n === 0) return m
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      )
    }
  }
  return dp[m][n]
}

/** Heuristic typo detection. Three layers:
 *  1. Exact known typo from the curated list (gmial.com etc) → instant fail
 *  2. Levenshtein ≤ 2 from a known provider with length guard → typo
 *  3. Repeated-char run of 4+ characters → looks like keyboard mash / fake
 *     (catches gmaddddddil.com which is too far for Levenshtein but
 *     visibly nonsense)
 */
function detectTypo(domain: string): boolean {
  if (!domain || domain.length < 6) return false
  // Known exact typos — fastest path
  const HARDCODED_TYPOS = [
    "gmial.com", "gnail.com", "gmal.com", "gmaill.com",
    "hotnail.com", "hotmial.com",
    "yaho.com", "yhoo.com", "yahooo.com",
    "outloo.com", "outloook.com", "outlok.com",
    "iclod.com", "icoud.com",
  ]
  if (HARDCODED_TYPOS.includes(domain)) return true

  // Exact match to known good → not a typo
  if (KNOWN_PROVIDERS.includes(domain)) return false

  // Repeated character run — gmaddddddil-style nonsense
  if (/(.)\1{3,}/.test(domain)) return true

  // Levenshtein vs known providers
  for (const candidate of KNOWN_PROVIDERS) {
    const distance = levenshtein(domain, candidate)
    if (distance === 1) return true
    if (distance === 2 && Math.abs(domain.length - candidate.length) <= 1) return true
  }
  return false
}

export default function HeroLiveValidator() {
  const [email, setEmail] = useState("")
  const [verdict, setVerdict] = useState<null | {
    ok: boolean
    label: string
    checks: { k: string; ok: boolean | "warn" }[]
  }>(null)
  const [busy, setBusy] = useState(false)
  const [userTouched, setUserTouched] = useState(false)
  const cardRef = useRef<HTMLDivElement | null>(null)
  // Cursor-tracked glow position (CSS vars updated on mousemove). Mercury /
  // Vercel / Apple use this to make a card feel "alive" without parallax.
  const [glow, setGlow] = useState<{ x: number; y: number; on: boolean }>({ x: 50, y: 50, on: false })

  const FM = "'JetBrains Mono', ui-monospace, monospace"
  const ACC = "#0F5132"
  const ACC_GLOW = "rgba(15,81,50,0.18)"
  const ACC_CHIP = "#DFF5E5"
  const BAD = "#A02B1F"
  const BAD_SOFT = "rgba(160,43,31,0.10)"
  const WARN = "#B85C00"
  const HAIR = "rgba(10,10,10,0.08)"
  const HAIR2 = "rgba(10,10,10,0.14)"
  const INK2 = "rgba(10,10,10,0.62)"
  const INK3 = "rgba(10,10,10,0.42)"
  const BG_SOFT = "#F4F1E8"

  // ── Auto-cycling demo ─────────────────────────────────────────
  // Every ~4.5s, type out a new example email char-by-char and run the
  // validator automatically. Cycles through the four interesting verdict
  // classes (deliverable, role, disposable, typo) so the hero is alive
  // without the user lifting a finger. Stops the moment the user types,
  // pastes, or focuses the field.
  const DEMO_EMAILS = useMemo(
    () => ["founder@databridge.so", "info@acme.io", "test@mailinator.com", "sara@gmial.com"],
    [],
  )
  useEffect(() => {
    if (userTouched) return
    // Honour prefers-reduced-motion + tab visibility. Users who opt
    // out of motion shouldn't see auto-typing; users with the tab
    // backgrounded shouldn't burn CPU on a demo they can't see.
    if (typeof window !== "undefined") {
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      if (reduced) return
      // Small phones (≤ 420 CSS px — iPhone SE 2020, older androids,
      // fold cover) don't have the vertical room for the cycling
      // verdict reveal: each open/close animation pushes the primary
      // "Clean my first list — free" CTA below the fold for the
      // ~3s the strip is visible. Keep the validator rendered (input
      // + button stay tappable) but skip the auto-cycle entirely so
      // the card stays at its compact resting height. Users can
      // still type and run a verdict manually if they want to.
      const narrow = window.matchMedia?.("(max-width: 420px)").matches
      if (narrow) return
    }
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return

    let cancelled = false
    let demoIndex = 0
    let typeTimer: ReturnType<typeof setTimeout> | null = null
    let cycleTimer: ReturnType<typeof setTimeout> | null = null

    const typeOut = (target: string, charIdx: number) => {
      if (cancelled || userTouched) return
      setEmail(target.slice(0, charIdx))
      if (charIdx < target.length) {
        typeTimer = setTimeout(() => typeOut(target, charIdx + 1), 55 + Math.random() * 40)
      } else {
        // run the validator after a beat so the typed value is visible
        cycleTimer = setTimeout(() => {
          if (cancelled || userTouched) return
          run(target)
          // schedule next cycle
          cycleTimer = setTimeout(() => {
            if (cancelled || userTouched) return
            demoIndex = (demoIndex + 1) % DEMO_EMAILS.length
            // brief clear then start typing the next one
            setVerdict(null)
            setEmail("")
            cycleTimer = setTimeout(() => typeOut(DEMO_EMAILS[demoIndex], 1), 350)
          }, 3200)
        }, 360)
      }
    }

    // first run starts after 800ms so the entrance animations settle
    cycleTimer = setTimeout(() => typeOut(DEMO_EMAILS[0], 1), 800)

    return () => {
      cancelled = true
      if (typeTimer) clearTimeout(typeTimer)
      if (cycleTimer) clearTimeout(cycleTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTouched, DEMO_EMAILS])

  function run(value: string) {
    const e = value.trim().toLowerCase()
    setBusy(true)
    setTimeout(() => {
      const syntax = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)
      const local = e.split("@")[0] || ""
      const domain = e.split("@")[1] || ""
      const role = ["info", "support", "admin", "sales", "contact", "hello", "help", "no-reply", "noreply"].includes(local)
      const disposable = ["mailinator.com", "tempmail.com", "10minutemail.com", "guerrillamail.com", "yopmail.com"].includes(domain)
      const typo = detectTypo(domain)
      const tld = /\.(com|net|org|io|co|dev|app|ai)$/.test(domain)
      // MX heuristic — can't do real DNS in the browser, so we use a
      // structural sanity check. Known-good providers pass. Anything
      // that already looks like a typo or has 4+ repeated chars
      // (gmaddddddil-style) gets flagged as fail. Real MX validation
      // happens on the worker for the full /try pipeline.
      const knownGood = KNOWN_PROVIDERS.includes(domain)
      const looksFake = /(.)\1{3,}/.test(domain) || domain.length > 40
      const mx = syntax && !disposable && !typo && !looksFake && (knownGood || (domain.length >= 4 && domain.length <= 30))
      const ok = syntax && !role && !disposable && !typo && mx

      setVerdict({
        ok,
        label: !syntax ? "Invalid syntax"
              : disposable ? "Disposable address"
              : typo ? "Likely typo"
              : role ? "Role-based — risky"
              : ok ? "Deliverable" : "At risk",
        // Short labels so the inline strip stays comfortable on 13"
        // laptops (~1440 CSS px). Full names ("MX record", "Role-based",
        // "Typo guard", "TLD trust") packed the row edge-to-edge against
        // the verdict pill — these compact ones leave breathing room
        // without losing meaning. The dot color still carries the
        // pass/fail/warn semantics.
        checks: [
          { k: "Syntax",  ok: syntax },
          { k: "MX",      ok: mx },
          { k: "Disposable", ok: !disposable },
          { k: "Role",    ok: !role },
          { k: "Typo",    ok: !typo },
          { k: "TLD",     ok: tld ? true : ("warn" as const) },
        ],
      })
      setBusy(false)
      // No auto-scroll on expand. The hero's top padding was pulled
      // up so the validator card + primary CTA both fit above the
      // fold even after the verdict strip appears. A scroll-on-
      // expand UX read as jarring (page moved under the cursor as
      // the user clicked Validate).
    }, 420)
  }

  // Pass/fail/warn glyph. Earlier this was a colored dot — the colour
  // alone carried the result, which fails WCAG 1.4.1 (Use of Color).
  // Now each state is a distinct shape (✓ / ✗ / !) inside the same
  // 10px footprint so SR users + colour-blind users get the signal
  // without leaning on hue.
  const glyph = (state: boolean | "warn", label: string) => {
    const color = state === "warn" ? WARN : state ? ACC : BAD
    const srText =
      state === "warn" ? `${label}: warning` : state ? `${label}: pass` : `${label}: fail`
    return (
      <span
        role="img"
        aria-label={srText}
        className="inline-flex items-center justify-center"
        style={{ width: 10, height: 10, color }}
      >
        {state === "warn" ? (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
            <circle cx="5" cy="5" r="4.25" stroke={color} strokeWidth="1.2" />
            <path d="M5 2.6v3" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
            <circle cx="5" cy="7.2" r="0.7" fill={color} />
          </svg>
        ) : state ? (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
            <circle cx="5" cy="5" r="4.25" fill={color} />
            <path d="M3.2 5.1l1.4 1.4 2.4-2.6" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
            <circle cx="5" cy="5" r="4.25" fill={color} />
            <path d="M3.4 3.4l3.2 3.2M6.6 3.4l-3.2 3.2" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        )}
      </span>
    )
  }

  return (
    <div
      ref={cardRef}
      onMouseMove={(e) => {
        const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
        setGlow({
          x: ((e.clientX - r.left) / r.width) * 100,
          y: ((e.clientY - r.top) / r.height) * 100,
          on: true,
        })
      }}
      onMouseLeave={() => setGlow((g) => ({ ...g, on: false }))}
      className="mt-6 relative w-full overflow-hidden"
      style={{
        background: "#FFFFFF",
        border: `1px solid ${HAIR2}`,
        borderRadius: 16,
        boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset, 0 30px 70px -30px rgba(10,10,10,0.18), 0 0 50px -22px ${ACC_GLOW}`,
      }}
    >
      {/* cursor-tracked emerald glow — fades in/out smoothly on hover */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          opacity: glow.on ? 1 : 0,
          background: `radial-gradient(420px circle at ${glow.x}% ${glow.y}%, rgba(15,81,50,0.10), transparent 55%)`,
        }}
      />
      {/* Ambient emerald sweep — runs whenever the hover-tracked glow
          isn't active. On desktop that's "before the user hovers"; on
          mobile (no mouse) that's always. Lets the card feel alive at
          every breakpoint without faking a touch event. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none overflow-hidden transition-opacity duration-300"
        style={{ opacity: glow.on ? 0 : 1 }}
      >
        <div
          className="absolute top-0 bottom-0 w-1/2 -left-1/4"
          style={{
            background:
              "radial-gradient(closest-side, rgba(15,81,50,0.16), rgba(15,81,50,0.06) 55%, transparent 75%)",
            animation: "try-live-ambient 6.5s ease-in-out infinite",
            filter: "blur(2px)",
          }}
        />
      </div>
      <style>{`
        @keyframes try-live-ambient {
          0%   { transform: translateX(0%);   opacity: 0 }
          12%  { opacity: 1 }
          88%  { opacity: 1 }
          100% { transform: translateX(260%); opacity: 0 }
        }
      `}</style>
      {/* gradient border highlight that follows the cursor too */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none transition-opacity duration-300 rounded-[16px]"
        style={{
          opacity: glow.on ? 1 : 0,
          background: `radial-gradient(280px circle at ${glow.x}% ${glow.y}%, rgba(15,81,50,0.22), transparent 60%)`,
          maskImage:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskImage:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor" as any,
          maskComposite: "exclude" as any,
          padding: "1px",
        }}
      />
      {/* Mobile: stack the chip on top, then input + button on the same
          row; the button hugs to ~96px so the input still has room.
          Desktop (sm+): everything on one row, Try-it-live chip first. */}
      <div className="relative flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 p-3 sm:p-3.5">
        <span
          className="self-start sm:self-auto inline-flex items-center px-2 py-0.5 rounded-md sm:ml-1.5"
          style={{
            fontFamily: FM, fontSize: 9.5, letterSpacing: "0.18em",
            textTransform: "uppercase", color: ACC, fontWeight: 700,
            background: ACC_CHIP, border: `1px solid rgba(15,81,50,0.25)`,
          }}
        >
          Try it live
        </span>
        <div className="flex items-center gap-2 sm:gap-3 sm:flex-1">
          <input
            type="email"
            value={email}
            onChange={(e) => { setUserTouched(true); setEmail(e.target.value) }}
            onFocus={() => setUserTouched(true)}
            onKeyDown={(e) => e.key === "Enter" && run(email)}
            placeholder="founder@databridge.so"
            spellCheck={false}
            autoComplete="off"
            aria-label="Email address to validate"
            className="flex-1 min-w-0 outline-none"
            style={{
              background: "transparent", color: "#0A0A0A", fontFamily: FM,
              fontSize: 14.5, padding: "10px 6px", border: "none",
            }}
          />
          <button
            onClick={() => run(email)}
            disabled={busy || !email}
            className="relative inline-flex items-center gap-1.5 transition-all flex-shrink-0 overflow-hidden"
            style={{
              background: busy ? BG_SOFT : ACC,
              color: busy ? INK2 : "#FAF7EE",
              padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
              opacity: !email ? 0.5 : 1,
              cursor: !email || busy ? "not-allowed" : "pointer",
              boxShadow: !email || busy ? "none" : "0 8px 18px -10px rgba(15,81,50,0.45)",
            }}
          >
            {/* Slow shimmer sweep — same hero-shimmer-x keyframes the
                main CTA uses. Suppressed only while busy so it doesn't
                compete with the Checking… spinner copy. Runs even when
                the input is empty — that's the whole point: ambient
                affordance that pulls the eye to the validator on
                mobile, where there's no hover glow to do that work. */}
            {!busy && (
              <span
                aria-hidden
                className="absolute inset-y-0 -left-1/4 w-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(110deg, transparent, rgba(255,255,255,0.42), transparent)",
                  animation: "hero-shimmer-x 3.5s ease-in-out infinite 1s",
                }}
              />
            )}
            <span className="relative inline-flex items-center gap-1.5">
              {busy ? "Checking…" : "Validate"}
              {!busy && (
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
          </button>
        </div>
      </div>

      {verdict && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="relative border-t"
          style={{
            borderColor: HAIR,
            animation: "hero-rise 380ms cubic-bezier(.2,.8,.2,1) both",
          }}
        >
          {/* Verdict strip — two-row layout that adapts cleanly to
              mobile. Row 1: result pill + ~420ms timing.
              Row 2: the six check chips, equally spaced via grid so
              they don't crowd or wrap awkwardly into a half-row.
              On sm+ both rows collapse into a single inline strip
              (pill+ms on the left, chips pushed right). Labels stay
              abbreviated (MX/Role/Typo/TLD) so even on small phones
              the chip row doesn't need to wrap. */}
          <div className="px-4 sm:px-5 py-2 sm:py-1.5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-1.5">
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md"
                style={{
                  background: verdict.ok ? ACC_CHIP : BAD_SOFT,
                  color: verdict.ok ? ACC : BAD,
                  border: `1px solid ${verdict.ok ? "rgba(15,81,50,0.28)" : "rgba(160,43,31,0.30)"}`,
                  fontFamily: FM, fontSize: 10, fontWeight: 700,
                  letterSpacing: "0.06em", textTransform: "uppercase",
                }}
              >
                {/* Tiny shape glyph carries pass/fail without relying
                    on hue alone — same semantic recipe as the chip
                    row below. Hidden from SR; the pill text already
                    spells the verdict ("Deliverable" / "At risk"). */}
                <span aria-hidden className="inline-flex">
                  {verdict.ok ? (
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <circle cx="5" cy="5" r="4.25" fill={ACC} />
                      <path d="M3.2 5.1l1.4 1.4 2.4-2.6" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  ) : (
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                      <circle cx="5" cy="5" r="4.25" fill={BAD} />
                      <path d="M3.4 3.4l3.2 3.2M6.6 3.4l-3.2 3.2" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                  )}
                </span>
                {verdict.label}
              </span>
              <span style={{ fontSize: 11, color: INK3, fontFamily: FM, letterSpacing: "0.04em" }}>
                ~420ms
              </span>
            </div>
            {/* Mobile: 6-column grid → every chip lives in its own
                cell so spacing is uniform and dots/labels never
                drift. Desktop: revert to inline flex with ml-auto. */}
            <div className="grid grid-cols-6 gap-x-1 sm:flex sm:flex-wrap sm:items-center sm:gap-x-2.5 sm:gap-y-1 sm:ml-auto">
              {verdict.checks.map((c) => (
                <div key={c.k} className="flex items-center justify-center sm:justify-start gap-1 min-w-0">
                  {glyph(c.ok, c.k)}
                  <span
                    className="truncate"
                    style={{ fontSize: 10.5, color: INK2, fontWeight: 500, letterSpacing: "0.01em" }}
                  >
                    {c.k}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
